<script setup lang="ts">
import {computed, type ComputedRef, type PropType} from "vue";
import type {SurveyPermittedAnswerModel} from "@/types/model.ts";

const props = defineProps({
  permittedAns: {
    type: Object as PropType<SurveyPermittedAnswerModel>,
    required: true
  },
  qId: {
    type: Number,
    required: true
  }
});

const syncValue = defineModel();

const color: ComputedRef<string | undefined> = computed(() => props.permittedAns?.renderingConfig.color),
  disable: ComputedRef<boolean> = computed(() => props.permittedAns?.renderingConfig.disabled ?? false),
  iconUrl: ComputedRef<string | undefined> = computed(() => props.permittedAns?.renderingConfig.iconUrl),
  name: ComputedRef<string | undefined> = computed(() => props.permittedAns?.name),
  subText: ComputedRef<string | undefined> = computed(() => props.permittedAns?.subText),
  value: ComputedRef<string | undefined> = computed(() => props.permittedAns?.renderingConfig.marker);
</script>

<template>
  <q-radio v-model="syncValue"
           :disable="disable"
           :id="`${props.qId}-${props.permittedAns?.answerId}`"
           :name="props.permittedAns?.renderingConfig.groupIdentifier || `group-${props.qId}`"
           :val="props.permittedAns?.answerId.toString()">
    
    <!-- Default Slot (Label) -->
    <template #default>
      
      <!-- Face -->
      <div v-if="iconUrl"
           class="col-auto face">
        <img :alt="name"
             :src="iconUrl" />
      </div>
      
      <!-- Number -->
      <div v-if="value"
           class="col-auto number">{{ value }}</div>
      
      <!-- Label -->
      <div :class="['col', {'pl-16' : iconUrl || value}, color !== undefined? 'pr-4' : 'pr-16', 'label']">
        
        <!-- Name -->
        <p>{{ name }}</p>
        
        <!-- SubText -->
        <p v-if="subText" class="subtext">{{ subText }}</p>
      </div>
      
      <!-- Color Bar -->
      <div v-if="color"
           class="col-auto bar" />
    </template>
  </q-radio>
</template>

<style scoped lang="scss">
@use 'sass:map';

.q-radio {
  $color: v-bind(color);

  width: 100%;
  
  // Inner
  :deep(.q-radio__inner) {
    display: none;
  }
  
  // Label
  :deep(.q-radio__label) {
    width: 100%;
    min-height: 76px;
    display: flex;
    flex-wrap: nowrap;
    font-weight: map.get($text-weights, semibold);
    font-size: map.get($font-sizes, '18');
    line-height: 22px;
    align-items: center;
    overflow: hidden;
    padding-inline-start: 16px;
    border: 1px solid $color;
    border-radius: $generic-border-radius-md;

    // Face
    .face {
      font-size: 0;
      line-height: 1;
      
      img {
        $size: 40px;
        
        width: $size;
        height: $size;
      }
    }
    
    // Number
    .number {
      font-weight: map.get($text-weights, medium);
      font-size: 44px;
      line-height: 1;
      color: $color;
    }
    
    // Label (Name & SubText)
    .label {
      padding-block: 16px;
      
      p {
        margin-block-end: 0;
        
        &.subtext {
          font-weight: map.get($text-weights, regular);
          font-size: map.get($font-sizes, '14');
          line-height: 18px;
          color: $content-txt-secondary;
        }
      }
    }
    
    // Color Bar
    .bar {
      $width: 12px;
      
      flex-basis: $width;
      width: $width;
      align-self: stretch;
      background-color: $color;
    }
  }
  
  :deep(.q-radio__inner--truthy) {
    
    + span + .q-radio__label {
      background-color: color-mix(in srgb, $color 5%, transparent);
      border-width: 2px;
    }
  }
}
</style>